import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import Fuse from 'fuse.js'

export interface Piece {
  id: string
  title: string
  catalogNumber?: string
  year?: number
  genre?: string
  searchTerms?: string[] // Optional for freetext entries
  composerId?: string
  source?: 'wikidata' | 'freetext' // Indicates if this is a freetext entry
}

interface ComposerWorksDataset {
  composerId: string
  composerName: string
  count: number
  generated: string
  works: Piece[]
}

/**
 * Hook to load a specific composer's works dataset
 *
 * @param composerId - Wikidata ID of the composer (e.g., "Q254" for Mozart)
 * @returns Query result with the composer's works
 */
export const useComposerWorksDataset = (composerId: string | null) => {
  return useQuery<ComposerWorksDataset>({
    queryKey: ['composer-works', composerId],
    queryFn: async () => {
      if (!composerId) {
        throw new Error('Composer ID is required')
      }

      const response = await fetch(`/data/composers/${composerId}.json`)

      if (!response.ok) {
        // 404 means we don't have a static file for this composer
        if (response.status === 404) {
          throw new Error(`No works data available for composer ${composerId}`)
        }
        throw new Error(`Failed to load works for composer ${composerId}: ${response.statusText}`)
      }

      return response.json()
    },
    enabled: !!composerId, // Only run query if composerId is provided
    staleTime: Infinity, // Never refetch - static dataset
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes (formerly cacheTime)
    retry: 1, // Only retry once for 404s
  })
}

/**
 * Hook for client-side piece autocomplete with fuzzy search
 *
 * @param composerId - Wikidata ID of the composer (e.g., "Q254")
 * @param searchTerm - The search term to match against piece titles
 * @param limit - Maximum number of results to return (default: 10)
 * @returns Array of matching pieces, sorted by relevance
 */
export const usePieceAutocomplete = (
  composerId: string | null,
  searchTerm: string,
  limit: number = 10,
): Piece[] => {
  const { data: dataset } = useComposerWorksDataset(composerId)

  const results = useMemo(() => {
    // Return empty array if no dataset or search term is too short
    if (!dataset?.works || searchTerm.length < 2) {
      return []
    }

    // Normalize function to remove accents and lowercase
    const normalize = (str: string) =>
      str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

    // Normalize search term and split into tokens
    const searchTokens = normalize(searchTerm)
      .split(/\s+/)
      .filter((token) => token.length > 0)

    // First pass: Filter items where ALL search tokens are present as substrings
    // This allows "Symphony 9" to match "Symphony No. 9" and "dvor" to match "Dvořák"
    const tokenMatches = dataset.works.filter((work) => {
      const normalizedTitle = normalize(work.title)
      const normalizedCatalog = work.catalogNumber ? normalize(work.catalogNumber) : ''
      const combinedText = `${normalizedTitle} ${normalizedCatalog}`

      // Check if all search tokens are present in the work
      return searchTokens.every((token) => combinedText.includes(token))
    })

    // If we have token matches, use them (already filtered by relevance)
    if (tokenMatches.length > 0) {
      return tokenMatches.slice(0, limit)
    }

    // Fallback to Fuse.js fuzzy search if no token matches
    // This handles typos and more complex fuzzy matching
    const fuse = new Fuse(dataset.works, {
      keys: [
        { name: 'title', weight: 3 },
        { name: 'catalogNumber', weight: 2 },
        { name: 'searchTerms', weight: 1 },
      ],
      threshold: 0.5,
      includeScore: true,
      ignoreLocation: true,
      minMatchCharLength: 2,
      isCaseSensitive: false,
      getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path)
        if (typeof value === 'string') {
          return normalize(value)
        }
        return value
      },
    })

    const fuseResults = fuse.search(normalize(searchTerm), { limit })
    return fuseResults.map((result) => result.item)
  }, [dataset, searchTerm, limit])

  return results
}

/**
 * Utility function to format piece display name with catalog number and year
 *
 * @param piece - The piece object
 * @returns Formatted string like "Symphony No. 40 (K. 550, 1788)"
 */
export const formatPieceTitle = (piece: Piece): string => {
  const { title, catalogNumber, year } = piece

  const details: string[] = []
  if (catalogNumber) {
    details.push(catalogNumber)
  }
  if (year) {
    details.push(year.toString())
  }

  if (details.length > 0) {
    return `${title} (${details.join(', ')})`
  }

  return title
}
